import { DocContent } from '../docsData';

const doc: DocContent = {
  title: 'Ellie Invoke API',
  content: `
The Ellie Invoke API lets you call Ellie programmatically — from another agent, a script, or any backend service — and get a structured JSON response instead of a streaming chat. Ellie runs to completion (tool calls and all) and returns her final answer along with references to any analyses or artifacts she created.

This is one of two ways to call Ellie from outside Pliable. Use this page if you're calling Ellie as a sub-agent from your own harness or orchestrator — one request in, one complete answer out. If you're building a human-facing chat interface instead, see [Ellie Streaming API](/docs/ellie-streaming-api) — that one streams tokens live and lets Ellie pause to ask a clarifying question, which fits a UI, not an autonomous caller.

---

## Authenticate

Every call below needs a bearer token. Exchange your API credentials for one:

\`\`\`
POST /api/v1/ext/authenticate
\`\`\`

\`\`\`bash
curl -X POST "https://app.pliable.co/api/v1/ext/authenticate" \\
  -H "Content-Type: application/json" \\
  -d '{
    "account_id": "your-tenant-slug",
    "client_id": "YOUR_CLIENT_ID",
    "client_secret": "YOUR_CLIENT_SECRET",
    "data_scope": {
      "dimension_filters": {"Region": ["US", "Canada"]},
      "blacklisted_metrics": []
    },
    "external_user_id": "portal-user-42"
  }'
\`\`\`

| Field | Type | Required | Description |
|---|---|---|---|
| \`account_id\` | string | Yes | Your tenant slug. |
| \`client_id\` / \`client_secret\` | string | Yes | Your API credentials (Settings → API Access). |
| \`data_scope\` | object | No | Restricts the token to specific \`dimension_filters\` / a \`blacklisted_metrics\` list instead of full tenant access. Omit for an unrestricted token. |
| \`external_user_id\` | string | No | Identifies one of *your* end-users. When set, conversations started with this token are attributed to that end-user — useful if your integration serves many of your own users and each should only see their own conversations. |

The response is \`{"access_token": "...", "token_type": "Bearer", "expires_in": 3600}\`. A scoped token is short-lived (1 hour, fixed) and can't be revoked before it expires — mint a fresh one per session rather than caching it long-term.

---

## Invoke Ellie

\`\`\`
POST /api/v1/ext/ellie-synchronous
\`\`\`

Runs Ellie synchronously. The request blocks until Ellie finishes all tool calls and produces her final response — the sub-agent pattern.

### Request

\`\`\`bash
curl -X POST "https://app.pliable.co/api/v1/ext/ellie-synchronous" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -d '{
    "prompt": "What were total sales last month by region?"
  }'
\`\`\`

| Field | Type | Required | Description |
|---|---|---|---|
| \`prompt\` | string | Yes (or \`messages\`) | The question or instruction for Ellie. |
| \`messages\` | array | Yes (or \`prompt\`) | Full message history in \`[{role, content}]\` format. Use for multi-turn conversations. |
| \`thread_id\` | string | No | Continue an existing thread, or supply a UUID of your own choosing to start a new one under a known id — this is what lets you watch it live (see below). Must be a real UUID if provided; omit it to have the server generate one for you. |
| \`model\` | string | No | LLM model override. Defaults to the tenant's configured Ellie model. |
| \`verbosity\` | string | No | \`"LOW"\`, \`"MEDIUM"\`, or \`"HIGH"\`. Controls how much Ellie narrates her process. |
| \`reasoning\` | string | No | Reasoning effort level for the LLM. |

> **Note:** When using \`prompt\`, it is sent as a single user message. Use \`messages\` when you need to pass conversation history or multi-turn context. A thread only belongs to the identity that started it — reusing someone else's \`thread_id\` is rejected.

### Response

\`\`\`json
{
  "thread_id": "a1b2c3d4-...",
  "response": "Total sales last month were $1.2M. The West region led with $450K, followed by...",
  "tool_calls": [
    {
      "id": "call_abc123",
      "name": "generate_and_run_analysis",
      "args": {
        "name": "Total Sales by Region - Last Month",
        "metrics": ["Total Sales"],
        "dimensions": ["Region"],
        "where_clauses": [],
        "visualization_type": "BAR",
        "date_range_start": "2026-06-01",
        "date_range_end": "2026-06-30"
      },
      "result": {
        "analysis_id": "667a1b2c3d4e5f..."
      }
    }
  ],
  "analyses": [
    {
      "analysis_id": "667a1b2c3d4e5f...",
      "data_url": "/api/v1/ext/analyses/667a1b2c3d4e5f.../data"
    }
  ],
  "artifacts": []
}
\`\`\`

| Field | Description |
|---|---|
| \`thread_id\` | Conversation thread ID. Pass this back in subsequent requests for multi-turn conversations. |
| \`response\` | Ellie's final text response with her analysis and interpretation. |
| \`tool_calls\` | Every tool call Ellie made during the run. Each entry includes the tool \`name\`, the \`args\` she passed, and a compact \`result\`. For \`generate_and_run_analysis\`, the result contains just the \`analysis_id\` — fetch the full data via the \`data_url\`. |
| \`analyses\` | Convenience list of all analyses created, with URLs to fetch their data. |
| \`artifacts\` | Any markdown artifacts (reports, memos) Ellie created, with URLs to fetch them. |

> **Tip:** Analysis data and artifact content are returned as URL references, not inline, to keep the response compact. Use the URLs to fetch the full data when needed.

---

## Watching it live

A synchronous call blocks until Ellie is done, so there's nothing to show a human watching it happen. If you generate \`thread_id\` yourself and open this *before* posting the synchronous call, you can watch it happen in real time:

\`\`\`
GET /api/v1/ext/ellie-synchronous/{thread_id}/live
\`\`\`

This is a Server-Sent Events (SSE) connection — it relays the same event vocabulary as the [Ellie Streaming API](/docs/ellie-streaming-api) (\`message.delta\`, \`tool.progress\`, \`message.done\`, \`needs_input\`, \`error\`) as your synchronous call actually runs, ending with a literal \`data: [DONE]\` line.

It's resumable: reconnect with the \`Last-Event-ID\` header (a browser \`EventSource\` sends this automatically on reconnect) to pick up exactly where you left off, without missing or repeating events — useful since a synchronous call can run for minutes, and a viewer may connect after it's already started or drop and reconnect partway through.

\`\`\`bash
curl -N "https://app.pliable.co/api/v1/ext/ellie-synchronous/YOUR_THREAD_ID/live" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"
\`\`\`

---

## Get Analysis Data

\`\`\`
GET /api/v1/ext/analyses/{analysis_id}/data
\`\`\`

Returns the visualization-ready data for an analysis using its saved default settings (date range, filters, sort order). This is the companion endpoint to the invoke response's \`data_url\` — call it to retrieve the actual data behind an analysis Ellie created. Works for any analysis in your tenant; your token's data_scope is applied at read time. An unknown \`analysis_id\` returns a 404.

### Request

\`\`\`bash
curl "https://app.pliable.co/api/v1/ext/analyses/667a1b2c3d4e5f.../data" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"
\`\`\`

| Parameter | Type | Required | Description |
|---|---|---|---|
| \`analysis_id\` | path | Yes | The analysis ID from the invoke response. |
| \`page\` / \`page_size\` | query | No | Pagination. |
| \`dimension_filters\` | query | No | JSON-encoded \`{dimension name: [allowed values]}\` to narrow this read only — doesn't change the analysis or trigger a rebuild. |

### Response

\`\`\`json
{
  "record": {
    "data": [...],
    "categories": [...],
    "series": [...]
  },
  "node_id": "node_abc123",
  "superset_id": null,
  "row_count": 42,
  "page": 1,
  "page_size": 1000
}
\`\`\`

> **Note:** This endpoint uses the analysis's own saved settings, narrowed by any \`dimension_filters\` you pass at read time. It's distinct from the internal, employee-facing analysis endpoints used inside the Pliable app.

---

## Fetch an Artifact

\`\`\`
GET /api/v1/ellieartifacts/{artifact_id}
\`\`\`

Returns the full artifact record, including the markdown content. Use the \`url\` from the invoke response's \`artifacts\` array.

> **Note:** Unlike the endpoints above, this one isn't yet part of the versioned public API surface and may not work with a data-scoped token in every case — reach out if your integration needs reliable external artifact access and we'll help.

\`\`\`json
{
  "record": {
    "id": "667b...",
    "name": "Q2 Sales Analysis Report",
    "description": "Comprehensive breakdown of Q2 performance by region",
    "markdown": "> **Ellie:** Here is your Q2 sales report...\\n\\n## 1. Overview\\n...",
    "created_at": "2026-07-08T14:35:00Z"
  }
}
\`\`\`

---

## Agent-to-Agent Behavior

When called via the invoke endpoint, Ellie runs in **agent-to-agent mode**. This changes her behavior:

- **No clarifying questions.** Ellie makes her best judgment instead of asking for clarification.
- **No confirmation prompts.** She won't ask "Does this match what you'd expect?"
- **No follow-up suggestions.** She answers directly without offering next steps.
- **Concise output.** She leads with data and interpretation, skipping conversational pleasantries.
- **Still uses the Knowledge Base.** She checks existing knowledge before running queries and saves new learnings when applicable.

### User Data Restrictions

If your token was minted with \`data_scope\`, those restrictions are automatically enforced on every query Ellie runs. Ellie is aware of these restrictions and will communicate them rather than silently presenting filtered data as if it were unfiltered.

### Superset Optimization

When a dashboard superset exists that covers the metrics and dimensions Ellie is querying, the invoke endpoint automatically skips the expensive per-analysis build and reads directly from the superset. This can reduce response times significantly for tenants with dashboard supersets enabled.

---

## Multi-Turn Conversations

To continue a conversation, pass the \`thread_id\` from the previous response:

\`\`\`bash
curl -X POST "https://app.pliable.co/api/v1/ext/ellie-synchronous" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -d '{
    "prompt": "Break that down by product category too",
    "thread_id": "a1b2c3d4-..."
  }'
\`\`\`

Ellie retains the full conversation context from the thread, so she knows what "that" refers to.

---

Building a chat UI instead of a sub-agent integration? See [Ellie Streaming API](/docs/ellie-streaming-api) for the streaming (SSE) entry point.
`,
};

export default doc;
